import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TagModel, BulkTagModel } from '../_models/tag.model';

import { environment } from '../../../../environments/environment';
import { UserService } from '../../auth/_services/user.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SystemDetailsService } from '../../system/system-details.service';

const API_TAG_URL = '/api/tag';
const API_URL = environment.API_URL;
const API_TAG_VALUES_URL = '/api/latest-tag-values';

export interface CSVHeader {
  id: string,
  title: string
}

export interface TagListRequest {
  page?: number;
  limit?: number | null;
  filters?: Record<string, any>;
  /** Sort map passed to POST /api/tag/list (`sort` on the request body). */
  sort?: Record<string, 1 | -1>;
  projections?: string[];
  csv?: boolean;
  csvHeaders?: CSVHeader[];
}

interface TagQueryOptions {
  page?: number;
  limit?: number | null;
  filters?: Record<string, any>;
  sortingOptions?: Record<string, 1 | -1> | null;
  projections?: string[] | null;
  csv?: boolean;
  csvHeaders?: CSVHeader[];
}


@Injectable({
  providedIn: 'root'
})
export class TagService {

  tagList: any[];
  tagListChanged = new EventEmitter<any[]>();
  isLoading = false;
  quickPlotData = [];
  isTagValueLoading: boolean = true;
  latestTagValueSubject = new BehaviorSubject<any>(null);
  latestTagValue = this.latestTagValueSubject.asObservable();
  iconColor : any = {};

  constructor(private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly systemDetailsService: SystemDetailsService) { }

  getOPCDADemoTags(){
    let url = 'https://osipi.uptimeai.com:5000/get-opcda-tags';
    return this.http.get(url);
  }

  getTagMetrics(table, range: any = {}) {
    let url = API_URL + '/api/tagmetrics?table=' + table;

    if (range.from)
      url += '&from=' + range.from;

    if (range.to)
      url += '&to=' + range.to;

    return this.http.get(url, {headers: this.userService.getHttpAuthHeader()});
  }


  getTagByUnits(units: string, all = false, fields: string = '') {
    let url = API_URL + API_TAG_URL;
    let params = new HttpParams()
      .set('unit', units)
      .set('populateSystem', '1')

    if (fields) {
      params = params.set('fields', fields);
    }
    if (all)
      params = params.set('all', true);
    return this.http.get(url, { params, headers: this.userService.getHttpAuthHeader() });
  }

  getTag(a, observableFlag = false, populateSystem = false, bySystemIds = false, plotType = '-1', fields = '') {
    let url = API_URL + API_TAG_URL;

    let params = new HttpParams();
    const param = bySystemIds ? 'system' : 'asset';
    if (a) {
      params = params.set(param, a);
    }
    if (populateSystem) {
      params = params.set('populateSystem', '1');
    }
    if (plotType == '2') {
      params = params.set('groupBy', 'templateId')
                      .set('fields', 'name,description,tagId,templateId,system');
    }
    if (fields) {
      params = params.set('fields', fields);
    }
    const options = {
      headers: this.userService.getHttpAuthHeader(),
      params: params
    };
    if (observableFlag) {
      return this.http.get(url, options);
    }

    this.isLoading = true;
    this.http.get(url, options).subscribe(
      (data: any) => {
        this.tagList = data.data;
        this.tagListChanged.emit(data.data);
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  getTagList(payload: TagListRequest) {
    const url = `${API_URL}${API_TAG_URL}/list`;
    const body: TagListRequest = { ...payload };
    return this.http.post(url, body, {
      headers: this.userService.getHttpAuthHeader(),
    });
  }

  getTagValues(reqData) {
    let url = `${API_URL}${API_TAG_VALUES_URL}`;
    return this.http.post(url, reqData, { headers: this.userService.getHttpAuthHeader() })
  }

  getTagListByTemplateId(a, observableFlag=false, populateSystem=false, bySystemIds = false, templateId, fields = null) {
    let url = API_URL + API_TAG_URL;
    let param = bySystemIds ? 'system' : 'asset';
    let httpParams = new HttpParams();

    if (a && populateSystem)
      httpParams = httpParams.set(param, a).set('populateSystem', '1');
    else if (a)
      httpParams = httpParams.set(param, a);
    else if (populateSystem)
      httpParams = httpParams.set('populateSystem', '1');

    httpParams = httpParams.set('templateId', templateId);

    if (fields) {
      httpParams = httpParams.set('fields', fields);
    }

    if (observableFlag) {
      return this.http.get(url, { params: httpParams, headers: this.userService.getHttpAuthHeader() });
    }
    this.isLoading = true;
    this.http.get(url, { params: httpParams, headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
      this.tagList = data.data;
      this.tagListChanged.emit(data.data);
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  getTags(systems, fields = '', groupBy = '', templateId = '', stringCurrent = false, tags = '') {
    let url = `${API_URL}${API_TAG_URL}`;
    let params = new HttpParams()
      .set('populateSystem', '1')
    if (systems) {
      params = params.set('system', systems)
    }
    if (fields) {
      params = params.set('fields', fields)
    }
    if (groupBy) {
      params = params.set('groupBy', groupBy)
    }
    if (templateId) {
      params = params.set('templateId', templateId);
    }
    if (stringCurrent)
      params = params.set('stringCurrent', true);
    if (tags) {
      params = params.set('_id',tags)
    }
    const headers = this.userService.getHttpAuthHeader();
    return this.http.get(url, { headers, params })
  }

  updateTagLastCleanedDate(updatedTagData) {
    let url = API_URL + API_TAG_URL + '/updateLastCleanedDate';
    return this.http.post(url, updatedTagData, { headers: this.userService.getHttpAuthHeader() });
  }
  createBulkTag(tagList) {

    // let tag = {
    //   name: t.name.trim(),
    //   description: t.description,
    //   unit: t.unit,
    //   tagId: t.tagId,
    //   asset: t.assetId,
    //   threshold: t.threshold,
    //   designValue: t.designValue
    // }

    return this.http.post<any>(API_URL + API_TAG_URL, tagList, { headers: this.userService.getHttpAuthHeader() })
  }

  createTag(t: TagModel) {
    let tag = {
      name: t.name.trim(),
      description: t.description,
      unit: t.unit,
      tagId: t.tagId,
      asset: t.assetId,
      threshold: t.threshold,
      thresholdAlertSettings: t.thresholdAlertSettings,
      opLimit: t.opLimit,
      alarmLimit: t.alarmLimit,
      thresholdAlertRange: t.thresholdAlertRange,
      trialRun: t.trialRun,
      designValue: t.designValue,
      calculated: t.calculated,
      parameters: t.parameters,
      constants: t.constants,
      formula: t.formula,
      displayExpression: t.displayExpression,
      expressionDescription: t.expressionDescription,
      system: t.system,
      direction: t.direction,
      iowCriticality: t.iowCriticality,
      iowCriticalityNew: t.iowCriticalityNew,
      dqDetection: t.dqDetection,
      dqAlertGeneration: t.dqAlertGeneration,
      connectGaps: t.connectGaps,
      thresholdNotification: t.thresholdNotification,
      ttiSetting: t.ttiSetting,
      kpi: t.kpi,
      stringCurrent: t.stringCurrent,
      stringCapacity: t.stringCapacity,
      attention: t.attention,
      internal: t.internal,
      customMultiLineExpression: t.customMultiLineExpression,
      eventCode: t.eventCode,
      csvUploadedValues: t.csvUploadedValues,
      customCode: t.customCode
    }

    return this.http.post<TagModel>(API_URL + API_TAG_URL, tag, { headers: this.userService.getHttpAuthHeader() })
  }

  updateTagModelling(d) {
    this.http.put<TagModel>(API_URL + API_TAG_URL, d, { headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
      //this.getTag(null);
    });
  }

  updateTag(t: TagModel) {
    let tag = {
      _id: t._id,
      name: t.name?.trim(),
      description: t.description,
      unit: t.unit,
      tagId: t.tagId,
      asset: t.assetId,
      threshold: t.threshold,
      thresholdAlertSettings: t.thresholdAlertSettings,
      trialRun: t.trialRun,
      opLimit: t.opLimit,
      alarmLimit: t.alarmLimit,
      thresholdAlertRange: t.thresholdAlertRange,
      designValue: t.designValue,
      calculated: t.calculated,
      parameters: t.parameters,
      constants: t.constants,
      formula: t.formula,
      displayExpression: t.displayExpression,
      expressionDescription: t.expressionDescription,
      system: t.system,
      customMultiLineExpression: t.customMultiLineExpression,
      direction: t.direction,
      iowCriticality: t.iowCriticality,
      iowCriticalityNew: t.iowCriticalityNew,
      dqDetection: t.dqDetection,
      dqAlertGeneration: t.dqAlertGeneration,
      connectGaps: t.connectGaps,
      thresholdNotification: t.thresholdNotification,
      ttiSetting: t.ttiSetting,
      kpi: t.kpi,
      stringCurrent: t.stringCurrent,
      stringCapacity: t.stringCapacity,
      attention: t.attention,
      remark: t.remark,
      eventCode: t.eventCode,
      csvUploadedValues: t.csvUploadedValues,
      interpolationInfo: t.interpolationInfo,
      customCode: t.customCode
    }

    return this.http.put<any>(API_URL + API_TAG_URL, tag, { headers: this.userService.getHttpAuthHeader() })
  }

  updateBulkTag(t: BulkTagModel) {
    let tag = {...t}
    return this.http.put<any>(API_URL + API_TAG_URL, tag, { headers: this.userService.getHttpAuthHeader() })
  }

  copyBulkDataFromAssets(assetIdMap, copyNodeWebId, copyTagId) {
    const url = API_URL + API_TAG_URL + '/copyBulkDataFromAssets';
    return this.http.post<any>(url, { assetIdMap, copyNodeWebId, copyTagId }, { headers: this.userService.getHttpAuthHeader() })
  }

  updateTagMappings(data: {
    unMappedSystemTags: string[];
    newMappedSystemTags: any[];
    bulkMapping: boolean;
    system: string;
  }) {
    return this.http.post<any>(API_URL + '/api/tag/updateMappings', data, {
      headers: this.userService.getHttpAuthHeader(),
    });
  }

  deleteTag(t: string) {
    return this.http.delete<TagModel>(API_URL + API_TAG_URL + '?_id=' + t, { headers: this.userService.getHttpAuthHeader() })
  }

  updateMultipleTags(reqBody: any) {
    let url = API_URL + '/api/tag/multiple';
    return this.http.put(url, reqBody, { headers: this.userService.getHttpAuthHeader() });
  }

  startAnnotationForTags(reqBody:any) {
    let url = API_URL+ '/api/cognite/highlight';
    return this.http.post(url, reqBody, { headers: this.userService.getHttpAuthHeader() })
  }

  getAnnotationForTags(jobId: any) {
    let url = API_URL + '/api/cognite/getData';
    let params = new HttpParams()
      .set('jobId', jobId)
    return this.http.get(url, {headers: this.userService.getHttpAuthHeader(), params});
  }

  startAnnotationToSvgConversion(jobId: any) {
    let url = API_URL + '/api/cognite/convert';
    let params = new HttpParams()
      .set('jobId', jobId)
    return this.http.post(url, {},{headers: this.userService.getHttpAuthHeader(), params});
  }

  getAnnotatedSvg(jobId: any) {
    let url = API_URL + '/api/cognite/getSvg';
    let params = new HttpParams()
      .set('jobId', jobId)
    return this.http.get(url, {headers: this.userService.getHttpAuthHeader(), params});
  }

  fetchSvgBlob(url: string): Observable<any> {
    return this.http.get(url, { responseType: 'text' });
  }

  getLatestTagValue(tagBody) {
    let url = API_URL + '/api/latest-tag-values';
    return this.http.post(url, tagBody, { headers: this.userService.getHttpAuthHeader() });
  }

  createLatestTagApiPayload(parameters: any, tsTable: string, parameterTags: any[] = []): any {
    this.isTagValueLoading = true;
    const body = {
      tsTable: tsTable?.trim() || "",
      calc: [],
      raw: []
    };
    // Populate calc and raw arrays
    Object.values(parameters || {}).forEach(value => {
      let tag = parameterTags.find(tag => tag.tagId === value);
      if (!tag) {
        tag = this.tagList.find(tag => tag.tagId === value);
      }
      if (tag) {
        tag.calculated ? body.calc.push(tag.tagId) : body.raw.push(tag.tagId);
      }
    });
    // Only make API call if conditions are met
    if (body.tsTable && (body.calc.length > 0 || body.raw.length > 0)) {
      this.getLatestTagValue(body).subscribe((res: any) => {
        // this.latestTagValue = res.tagValues; // Set latestTagValue once API call completes
        this.latestTagValueSubject.next(res.tagValues);
      });
    } else this.latestTagValueSubject.next({});
  }

  getUpdatedYAxisRange(minValue?: number | null, maxValue?: number | null): { yaxis: { range?: [number?, number?] } } {
    let newMin: number | undefined;
    let newMax: number | undefined;
    const hasMin = minValue !== null && minValue !== undefined;
    const hasMax = maxValue !== null && maxValue !== undefined;

    if (hasMin && hasMax) {
      const range = Math.abs(maxValue - minValue);
      const marginRatio = range > 1 ? 0.2 : 0.5;
      const margin = range * marginRatio == 0 ? 1 : range * marginRatio;
      newMin = Math.floor(minValue - margin);
      newMax = Math.ceil(maxValue + margin);
    } else {
      newMin = hasMin ? minValue! : undefined;
      newMax = hasMax ? maxValue! : undefined;
    }
    return {
      yaxis: {
        range: [newMin, newMax]
      }
    };
  }

  getTagsWithPagination(tagOptions: TagQueryOptions): Observable<any> {
    const {
      page = 1,
      limit = null,
      filters = {},
      sortingOptions = null,
      projections = null,
      csv = false,
      csvHeaders = [],
    } = tagOptions;
    const url = API_URL + API_TAG_URL + '/list';
    let requestBody = {};
    const options: any = {
      headers: this.userService.getHttpAuthHeader()
    };
    if (limit) {
      requestBody = { page, limit };
    }
    if (filters) {
      requestBody["filters"] = filters;
    }
    if (sortingOptions) {
      requestBody["sort"] = sortingOptions;
    }
    if (csv) {
      requestBody["csv"] = true;
      requestBody["csvHeaders"] = csvHeaders;
      options.responseType = 'text';
    }
    if (projections) {
      requestBody["projections"] = projections;
    }
    return this.http.post(url, requestBody, options);
  }

  /** Fetch {tagId, system[]} records for every tag sharing the given historian identifiers.
   *  Used by What-If KPI scope discovery to build system→tagId pairs so each sibling
   *  system is matched only against its own parameter naming. */
  getTagPairsByHistorianPoint(webIds: string[], nodeIds: string[]): Observable<any> {
    const url = API_URL + API_TAG_URL + '/tag-pairs-by-historian-point';
    const body: any = {};
    if (webIds.length) body.webIds = webIds;
    if (nodeIds.length) body.nodeIds = nodeIds;
    return this.http.post(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  getTagCountBySystem(filters: any): Observable<any> {
    const url = API_URL + API_TAG_URL + '/tagCountBySystem';
    const requestBody = {};
    if (filters) {
      requestBody['filters'] = filters;
    }
    return this.http.post(url, requestBody, { headers: this.userService.getHttpAuthHeader() });
  }

  getTagsOverviewCount(filters: any, extraFilters?: any): Observable<any> {
    const url = `${API_URL}${API_TAG_URL}/overview-count`;
    const requestBody: any = {};
    if (filters) {
      requestBody["filters"] = filters;
    }
    if (extraFilters) {
      requestBody["extraFilters"] = extraFilters;
    }
    return this.http.post(url, requestBody, { headers: this.userService.getHttpAuthHeader() });
  }

  getDataHealthOverview(filters: any): Observable<any> {
    const url = API_URL + API_TAG_URL + '/overview';
    const requestBody = {};
    if (filters) {
      requestBody['filters'] = filters;
    }
    return this.http.post(url, requestBody, { headers: this.userService.getHttpAuthHeader() });
  }

  setTraceDataForScroll(tagGraph: any, currentTagGraph: any, traceIndex: number, xData: any[], yData: any[], isGraphLoaded: boolean) {
    const xaxisScroll = this.systemDetailsService.xaxisScroll;
    // currentTagGraph (the previously rendered graph) can be null/undefined if the
    // graph wasn't fully loaded yet — fall back to empty arrays instead of throwing
    // "Cannot read properties of null (reading 'data')".
    const prevTrace = currentTagGraph?.data?.[traceIndex];
    if (isGraphLoaded && xaxisScroll == 1) {
      tagGraph.data[traceIndex].x = [...xData, ...(prevTrace?.x ?? [])];
      tagGraph.data[traceIndex].y = [...yData, ...(prevTrace?.y ?? [])];
    } else if (isGraphLoaded && xaxisScroll == 2) {
      tagGraph.data[traceIndex].x = [...(prevTrace?.x ?? []), ...xData];
      tagGraph.data[traceIndex].y = [...(prevTrace?.y ?? []), ...yData];
    } else if (!isGraphLoaded) {
      tagGraph.data[traceIndex].x = xData;
      tagGraph.data[traceIndex].y = yData;
    } else {
      tagGraph.data[traceIndex].x = [...(prevTrace?.x ?? []), ...xData];
      tagGraph.data[traceIndex].y = [...(prevTrace?.y ?? []), ...yData];
    }
  }
}
